import { saasApiAxios } from '../lib/axios-interceptor';

export interface CustomerUser {
  id: string;
  customer_id: string;
  role_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  auth_provider: 'local';
  email_verified: boolean;
  last_login_at?: number;
  status: 'active' | 'inactive';
  custom: boolean;
  created_by?: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
  role?: {
    id: string;
    name: string;
    description?: string;
    permissions: Record<string, boolean>;
    customer_id: string;
  };
  role_name?: string;
  customer_name?: string;
}

export interface CreateCustomerUserRequest {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  role_id: string;
  status?: string;
  avatar_url?: string;
}

export interface UpdateCustomerUserRequest {
  first_name?: string;
  last_name?: string;
  role_id?: string;
  status?: string;
  avatar_url?: string;
  password?: string;
}



const baseURL = '/api/customer-users';

// CRUD functions
export const getCustomerUsers = async (): Promise<CustomerUser[]> => {
  try {
    const response = await saasApiAxios.get(baseURL);
    return response.data;
  } catch (error) {
    console.error('Error fetching customer users:', error);
    throw error;
  }
};

export const getCustomerUser = async (id: string): Promise<CustomerUser> => {
  try {
    const response = await saasApiAxios.get(`${baseURL}/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching customer user:', error);
    throw error;
  }
};

export const createCustomerUser = async (userData: CreateCustomerUserRequest): Promise<CustomerUser> => {
  try {
    const response = await saasApiAxios.post(baseURL, userData);
    return response.data;
  } catch (error) {
    console.error('Error creating customer user:', error);
    throw error;
  }
};

export const updateCustomerUser = async (id: string, userData: UpdateCustomerUserRequest): Promise<CustomerUser> => {
  try {
    const response = await saasApiAxios.put(`${baseURL}/${id}`, userData);
    return response.data;
  } catch (error) {
    console.error('Error updating customer user:', error);
    throw error;
  }
};

export const deleteCustomerUser = async (id: string): Promise<void> => {
  try {
    await saasApiAxios.delete(`${baseURL}/${id}`);
  } catch (error) {
    console.error('Error deleting customer user:', error);
    throw error;
  }
};

// Helper function to check if user is a system user (cannot be modified)
export const isDefaultUser = (user: CustomerUser): boolean => {
  // System users have custom = false, custom users have custom = true
  return !user.custom;
};

// Helper function to get user display name
export const getUserDisplayName = (user: CustomerUser): string => {
  if (user.first_name && user.last_name) {
    return `${user.first_name} ${user.last_name}`;
  } else if (user.first_name) {
    return user.first_name;
  } else if (user.last_name) {
    return user.last_name;
  } else {
    return user.email;
  }
};

// Helper function to get user initials
export const getUserInitials = (user: CustomerUser): string => {
  if (user.first_name && user.last_name) {
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  } else if (user.first_name) {
    return user.first_name.charAt(0).toUpperCase();
  } else if (user.last_name) {
    return user.last_name.charAt(0).toUpperCase();
  } else {
    return user.email.charAt(0).toUpperCase();
  }
};

// Helper function to get auth provider icon
export const getAuthProviderIcon = (provider: string): string => {
  // Only local auth is supported for customer users
  return '🔐'; // Local auth icon
};

// Helper function to format last login
export const formatLastLogin = (lastLoginAt?: number): string => {
  if (!lastLoginAt) {
    return 'Never';
  }
  
  const date = new Date(lastLoginAt * 1000);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  
  if (diffInHours < 1) {
    return 'Just now';
  } else if (diffInHours < 24) {
    return `${diffInHours} hours ago`;
  } else {
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} days ago`;
  }
};

// Export as default object for backward compatibility
const CustomerUserService = {
  getCustomerUsers,
  getCustomerUser,
  createCustomerUser,
  updateCustomerUser,
  deleteCustomerUser,
  isDefaultUser,
  getUserDisplayName,
  getUserInitials,
  getAuthProviderIcon,
  formatLastLogin
};

export default CustomerUserService; 